import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import PanesHeader from 'modules/core/components/PanesHeader'
import PanesPadForm from 'modules/core/components/PanesPadForm'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'
import LegalEntity from 'modules/core/store/models/LegalEntity'

import { useEmployeePersonalProvider } from 'modules/employees/components/blocks/EmployeePersonalProvider'
import type Employee from 'modules/employees/store/models/Employee'

import BasicForm from './BasicForm'
import ExtraInformationForm from './ExtraInformationForm'
import { ScrollRowType } from './types'

interface Props {
  employee: Employee
  isSpanish: boolean
  readonly?: boolean
  errors?: Record<string, string[] | Record<string, string>> | undefined | null
  scrollTo?: ScrollRowType | null | undefined
  legalEntity?: LegalEntity | null
  canSee?: boolean
}

export const shouldShowTaxResidence = (employee: Employee, legalEntity?: LegalEntity | null) =>
  (employee.isEligibleForPayroll || hasFeature('payroll_bundle')) &&
  legalEntity?.country === LegalEntity.countryDefaults.es

export const TaxResidence = observer((props: Props) => {
  const { taxResidenceForm } = useEmployeePersonalProvider()
  const { employee, isSpanish, readonly = false, scrollTo, errors, canSee } = props

  React.useEffect(() => {
    errors && taxResidenceForm.setErrors(errors)
  }, [taxResidenceForm, errors])

  const isNotResident =
    taxResidenceForm.get('is_resident').value === false ||
    taxResidenceForm.get('is_resident').value === 'false'

  if (!canSee) return null

  return (
    <Box gap="s24">
      <BasicForm
        form={taxResidenceForm}
        employee={employee}
        readonly={readonly}
        isSpanish={isSpanish}
        scrollTo={scrollTo}
      />
      {isNotResident && (
        <ExtraInformationForm
          employee={employee}
          readonly={readonly}
          errors={errors}
          scrollTo={scrollTo}
        />
      )}
    </Box>
  )
})

const TaxResidenceWrapper = (props: Props) => {
  const canSee = shouldShowTaxResidence(props.employee, props.legalEntity)
  if (!canSee) return null
  return (
    <PanesPadForm direction="horizontal">
      <PanesHeader
        illustration="sign-post"
        title={i18n.t('employee_form.tax_residence.title')}
        description={i18n.tCurrent(
          'employee_form.tax_residence.description',
          employeeUser(props.employee).isCurrent()
        )}
      />
      <TaxResidence canSee={canSee} {...props} />
    </PanesPadForm>
  )
}

export default observer(TaxResidenceWrapper)
